import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sun, Moon, Zap, Wand2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CUISINES } from "@/lib/constants";

export default function AutofillDialog({ open, onOpenChange, start, end, onDone }) {
  const [midi, setMidi] = useState(true);
  const [soir, setSoir] = useState(true);
  const [quick, setQuick] = useState(false);
  const [cuisine, setCuisine] = useState("toutes");
  const [vary, setVary] = useState(false);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!midi && !soir) { toast.error("Choisissez au moins midi ou soir"); return; }
    setLoading(true);
    try {
      const res = await api.post("/meals/autofill", {
        start, end,
        include_midi: midi,
        include_soir: soir,
        quick_only: quick,
        max_time: 30,
        cuisine: vary || cuisine === "toutes" ? null : cuisine,
        vary_cuisine: vary,
      });
      const n = res.data.created;
      if (!n) toast.error("Aucune suggestion", { description: "Aucune recette ne correspond à ces réglages ou tout est déjà planifié." });
      else toast.success(`${n} repas suggéré${n > 1 ? "s" : ""} ✨`);
      onOpenChange(false);
      onDone && onDone();
    } catch {
      toast.error("Impossible de suggérer des repas");
    } finally {
      setLoading(false);
    }
  };

  const Toggle = ({ label, icon: Icon, val, set, testid }) => (
    <button data-testid={testid} onClick={() => set(!val)}
      className="flex items-center justify-between rounded-2xl border border-black/10 bg-white p-3 at-press">
      <span className="flex items-center gap-2 font-medium"><Icon size={18} style={{ color: "var(--at-honey)" }} /> {label}</span>
      <Switch checked={val} onCheckedChange={set} />
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl" data-testid="autofill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Réglages des suggestions</DialogTitle>
          <DialogDescription className="sr-only">Choisir les créneaux, les plats rapides et le type de cuisine</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Toggle label="Midis" icon={Sun} val={midi} set={setMidi} testid="af-midi" />
            <Toggle label="Soirs" icon={Moon} val={soir} set={setSoir} testid="af-soir" />
          </div>
          <Toggle label="Seulement les plats rapides (≤ 30 min)" icon={Zap} val={quick} set={setQuick} testid="af-quick" />
          <Toggle label="Varier les cuisines sur la semaine" icon={Shuffle} val={vary} set={setVary} testid="af-vary" />
          <div className={`space-y-1 transition-opacity ${vary ? "pointer-events-none opacity-40" : ""}`}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Type de cuisine</p>
            <Select value={cuisine} onValueChange={setCuisine} disabled={vary}>
              <SelectTrigger className="rounded-2xl border-black/10 bg-white" data-testid="af-cuisine"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="toutes">Toutes les cuisines</SelectItem>
                {CUISINES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="af-run-btn" onClick={run} disabled={loading}
            className="h-13 w-full rounded-2xl py-3 text-base font-semibold text-white at-press" style={{ background: "var(--at-honey)" }}>
            <Wand2 size={18} className="mr-2" /> {loading ? "Suggestion..." : "Compléter la semaine"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
